import { DOCUMENT } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import {
  FormGroupDirective,
  NgForm,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponent } from "src/app/moov-common/base/base.component";
import { Locale } from "src/app/moov-common/enum/locale.enum";
import { AbstractAuthorizationService } from "src/app/moov-common/services/authorization/authorization.abstract.service";
import { InternalEventsManagerService } from "src/app/moov-common/services/internal-events-manager/internal-events-manager.service";
import { MoovTokenErrorCode } from "src/app/moov-common/interfaces";
import { AbstractLocalStorageService } from "src/app/moov-common/services/localStorage/local-storage.abstract.service";
import { LocalStorageKeys } from "src/app/moov-common/services/localStorage/local-storage.enum";
import { AccountTypeEnum, DiscoveryStatusEnum, UserRole } from "src/app/moov-common/services/personal-user-data/personal-user-data";
import { AbstractPersonalUserDataService } from "src/app/moov-common/services/personal-user-data/personal-user-data.abstract.service";
import { NAVIGATION_ROUTES } from "src/app/navigation-routes.const";

@Component({
    selector: "app-token-login",
    templateUrl: "./token-login.component.html",
    styleUrls: ["./token-login.component.scss"],
})
export class TokenLoginComponent extends BaseComponent implements OnInit {
    isLogin: boolean = false;
    errorMessage: any;
    jwtToken: string;
    imageRootFolder = "assets/common/";
    accessToken: string = "";
    password: string = "";
    lastLoginFailedOnCreds: boolean = false;
    registrationFailed: boolean = false;
    isTokenValidated: boolean = false;
    userLocaleOveride = "";
    localeValues = Locale;
    hidePassword: boolean = true;
    errMessage: string = "";
    isSubmitting: boolean = false;
    matcher = new MyErrorStateMatcher();
    selectedLocale: string;
    isB2CMode: boolean = false;
    customerId: string = "";
    isWorkingWithCustomerId: boolean = false;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private localStorageService: AbstractLocalStorageService,
        private authorizationService: AbstractAuthorizationService,
        private router: Router,
        private http: HttpClient,
        private personalUserDataService: AbstractPersonalUserDataService,
        private internalEventService: InternalEventsManagerService,
        private translate: TranslateService,
        public formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    get tokenFormUserEmail() {
        return this.tokenLoginForm.get("tokenFormUserEmail");
    }

    tokenLoginForm = this.formBuilder.group(
        {
            tokenFormUserEmail: [
                "",
                [Validators.required, Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[A-Za-z]{2,4}$")],
            ],
            tokenFormFirstName: new UntypedFormControl(""),
            tokenFormLastName: new UntypedFormControl(""),
            tokenFormUserInputOrg: new UntypedFormControl(""),
            tokenFormUserInputRole: new UntypedFormControl(""),
            tokenFormGender: ["", Validators.required],
            tokenFormLocale: ["", Validators.required],
            tokenFormRole: ["", Validators.required],
            password: ["", [Validators.required]],
            confirmPassword: [""],
        },
        { validator: this.checkPasswords },
    );

    checkPasswords(group: UntypedFormGroup) {
        // here we have the 'passwords' group
        let pass = group.controls.password.value;
        let confirmPass = group.controls.confirmPassword.value;

        return pass === confirmPass ? null : { passwordDiffer: true };
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.selectedLocale = Locale.en_US;
        this.lastLoginFailedOnCreds = false;
        this.registrationFailed = false;
        this.checkLoggedInOrInDiscovery();
    }

    ngAfterViewInit(): void {
        this.route.queryParams.subscribe((params) => {
            let tokenBody = params.tokenBody;
            let mode = params.mode;
            let customerId = params.customerId;

            // Detect B2C mode
            if (mode === "b2c") {
                this.isB2CMode = true;
            } else {
                // Default to B2B mode
                this.isB2CMode = false;
            }
            // Add required validators based on mode
            this.addRequiredValidators();

            
            if (customerId != undefined) {
                this.isWorkingWithCustomerId = true;
                this.customerId = customerId;
                this.verifyCustomerId();
            }
            else {
                // no customerId, we need to verify the token
                if (tokenBody == undefined) {
                    // console.log(`in TV onInit dbgCounterpartId is undefined`);
                } else if (!this.isUserLogin()) {
                    // Only submit token if user is not already logged in (avoids re-submitting after registration reload)
                    this.accessToken = tokenBody;
                    this.submitToken();
                }
        }

            // Trigger change detection to avoid NG0100 error
            this.cdr.detectChanges();
        });
    }

    isUserLogin() {
        return this.user ? true : false;
    }

    checkLoggedInOrInDiscovery() {
        if (this.isUserLogin()) {
            this.redirectToUrl();
        }
    }

    verifyCustomerId(): void {
        this.authorizationService.validateTokenlessRegistrationForCustomer(this.customerId).subscribe(
            (response : boolean) => {
                console.log(`in TL verifyCustomerId response is ${response}`);
                if (response) {
                    this.isTokenValidated = true;
                    this.lastLoginFailedOnCreds = false;
                }
                else {
                    this.isTokenValidated = false;
                    this.errorMessage = this.translate.instant("tokenLogin.customerIdFailedValidation");
                    this.lastLoginFailedOnCreds = true;
                }
            },
            (error : any) => {
                this.isTokenValidated = false;
                this.errorMessage = error.error.detail;
                this.lastLoginFailedOnCreds = true;
            }
        );
    }

    private redirectToUrl() {
        let redirectTo = this.getUserHomePage();

        if (this.user?.accountType === AccountTypeEnum.CAREER_INDEPENDENT || this.user?.accountType === AccountTypeEnum.CAREER_INDEPENDENT_US) {
            // B2C users go directly to the process view
            redirectTo = NAVIGATION_ROUTES.app.processView;
        } else if (this.user?.discoveryStatus !== DiscoveryStatusEnum.DONE) {
            // user have yet to finish the discovery process - redirect to first login page
            redirectTo = NAVIGATION_ROUTES.app.firstTimeLogin;
        }

        this.router.navigate([redirectTo]);
    }

    getUserHomePage(): string {
        if (this.user?.role == UserRole.EMPLOYEE) {
            return NAVIGATION_ROUTES.app.personalView;
        } else {
            return NAVIGATION_ROUTES.app.teamView;
        }
    }

    switchToLogin(): void {
        this.router.navigate([NAVIGATION_ROUTES.login]);
    }

    submitToken(): void {
        // console.log (`in TL  submit accessToken is ${this.accessToken}`);

        this.authorizationService.tokenLogin(this.accessToken).subscribe(
            (response) => {
                if (response) {
                    this.isTokenValidated = true;
                    this.lastLoginFailedOnCreds = false;
                    this.analytics.tokenLoginEvent();
                } else {
                    this.lastLoginFailedOnCreds = true;
                }
            },
            (error) => {
                if (error.error?.detail === "token_already_used") {
                    this.router.navigate([NAVIGATION_ROUTES.login]);
                    return;
                }

                this.lastLoginFailedOnCreds = true;
            },
        );
    }

    setFormLocale(locale: string): void {
        this.personalUserDataService.setLocale(locale); // this also writes to local storage
        // window.location.reload(); no need to reload the page,
    }

    get isRTL(): boolean {
        console.log(`isRTL is called, userLocaleOveride is ${this.userLocaleOveride}`);
        if (!this.user && (this.userLocaleOveride == undefined || this.userLocaleOveride == "")) {
            let locale = this.utilsService.getBrowserLocale();

            if (locale === Locale.he_IL) {
                return true;
            } else {
                return false;
            }
        } else if (!this.user && this.userLocaleOveride != "") {
            if (this.userLocaleOveride === Locale.he_IL) {
                return true;
            } else {
                return false;
            }
        } else {
            // user is logged in
            return super.isRTL;
        }
    }

    // setRTL() {
    //   // console.log (`setRTL #1 document.body.style.direction is ${document.body.style.direction}`)
    //   // this.isRTL ? document.body.style.direction = 'rtl' : document.body.style.direction = 'ltr';

    //   // console.log (`setRTL #2 document.body.style.direction is ${document.body.style.direction}`)
    // }

    getLocaleForPresentation(locale: string): string {
        if (locale == "en-US") {
            return "English (US)";
        } else {
            // locale == "he-IL"
            return "Hebrew (IL)";
        }
    }

    openPrivacyPolicy(): void {
        const localePath = this.translate.currentLang === "he-IL" ? "he" : "en";
        const url = `/assets/static/discovery/privacy/${localePath}/Privacy_Policy.html`;
        window.open(url, "_blank");
    }

    openTermsOfUse(): void {
        const localePath = this.translate.currentLang === "he-IL" ? "he" : "en";
        const url = `/assets/static/discovery/terms/${localePath}/Terms.html`;
        window.open(url, "_blank");
    }

    private addRequiredValidators(): void {
        const orgControl = this.tokenLoginForm.get("tokenFormUserInputOrg");
        const roleControl = this.tokenLoginForm.get("tokenFormUserInputRole");

        const mgmtRoleControl = this.tokenLoginForm.get("tokenFormRole");

        // Org, role, and managerial role fields are only required for B2B mode
        if (!this.isB2CMode) {
            if (orgControl) {
                orgControl.setValidators([Validators.required]);
                orgControl.updateValueAndValidity();
            }

            if (roleControl) {
                roleControl.setValidators([Validators.required]);
                roleControl.updateValueAndValidity();
            }
        } else {
            // In B2C mode, remove required validators from org, role, and managerial role fields
            if (orgControl) {
                orgControl.setValidators(null);
                orgControl.updateValueAndValidity();
            }

            if (roleControl) {
                roleControl.setValidators(null);
                roleControl.updateValueAndValidity();
            }

            if (mgmtRoleControl) {
                mgmtRoleControl.setValidators(null);
                mgmtRoleControl.updateValueAndValidity();
            }
        }

    }

    registerUser(): void {
        this.isSubmitting = true;

        let isInMgmtRole = this.tokenLoginForm.value.tokenFormRole == 1;
        let isShareWithConsultants = true;

        this.authorizationService
            .registerUserFromToken(
                this.accessToken,
                this.customerId,
                this.tokenLoginForm.value.tokenFormFirstName,
                this.tokenLoginForm.value.tokenFormLastName,
                this.tokenLoginForm.value.tokenFormUserEmail,
                this.tokenLoginForm.value.tokenFormUserInputOrg,
                this.tokenLoginForm.value.tokenFormUserInputRole,
                this.tokenLoginForm.value.tokenFormLocale,
                this.tokenLoginForm.value.tokenFormGender,
                isInMgmtRole,
                isShareWithConsultants,
                this.tokenLoginForm.value.password,
            )
            .subscribe(
                (response) => {
                    if (response.status === 200 && response.body) {
                        this.lastLoginFailedOnCreds = false;
                        this.registrationFailed = false;
                        this.errMessage = "";
                        const result = response.body;
                        this.jwtToken = result.token;
                        this.localStorageService.write<string>(LocalStorageKeys.moovToken, this.jwtToken);
                        this.authorizationService.saveUserDetailsLocally(this.jwtToken);
                        this.personalUserDataService.setLocale("");
                        this.internalEventService.userLoggedIn.emit();
                        this.internalEventService.reloadUserData.emit("");
                        this.isSubmitting = false;
                        this.analytics.loginEvent();
                        this.analytics.userSelfRegistered();
                        // in case the user selected a different locale, the whole site is reloaded. also the redriect to URL will be triggered
                        window.location.reload();
                    } else {
                        this.isSubmitting = false;
                        this.registrationFailed = true;
                        if (response.status === 400) {
                            this.errMessage = this.translate.instant("tokenLogin.registrationFailed");
                        } else if (response.status === 401) {
                            this.errMessage = this.translate.instant("tokenLogin.passwordIssues");
                        }
                    }
                },
                (error) => {
                    this.isSubmitting = false;
                    console.log(`in TL registerUser failed error status is ${error.status}`);
                    this.registrationFailed = true;
                    if (error.error?.errorCode === MoovTokenErrorCode.B2B_PERSONAL_EMAIL_DOMAIN_NOT_ALLOWED) {
                        this.errMessage = this.translate.instant("tokenLogin.businessMailRequired");
                    } else if (error.status === 400) {
                        this.errMessage = this.translate.instant("tokenLogin.registrationFailed");
                    } else if (error.status === 401) {
                        this.errMessage = this.translate.instant("tokenLogin.passwordIssues");
                    }
                },
            );
    }
}

export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const invalidCtrl = !!(control && control.invalid && control.parent && control.parent.dirty);
        const invalidParent = !!(control && control.parent && control.parent.invalid && control.parent.dirty);

        return invalidCtrl || invalidParent;
    }
}
